import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * File Upload Component
 * Reusable component for uploading images (avatars, logos, etc.)
 */
import React, { useState, useRef, useEffect } from 'react';
import { Upload, X, Check, AlertCircle, Image as ImageIcon } from 'lucide-react';
export function FileUpload({ currentImageUrl, onFileSelected, onUpload, onRemove, maxSize = 5 * 1024 * 1024, // 5MB default
acceptedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'], shape = 'square', size = 'md', label, helperText, disabled = false, }) {
    const [preview, setPreview] = useState(currentImageUrl || null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const fileInputRef = useRef(null);
    // Sync preview with currentImageUrl prop changes
    useEffect(() => {
        if (currentImageUrl !== preview) {
            setPreview(currentImageUrl || null);
        }
    }, [currentImageUrl]);
    const sizeClasses = {
        sm: 'w-20 h-20',
        md: 'w-32 h-32',
        lg: 'w-48 h-48',
    };
    const shapeClasses = {
        square: 'rounded-lg',
        circle: 'rounded-full',
        rectangle: 'rounded-lg',
    };
    const handleFileSelect = (event) => {
        const file = event.target.files?.[0];
        if (!file)
            return;
        setError(null);
        setSuccess(false);
        // Validate file size
        if (file.size > maxSize) {
            setError(`File size must be less than ${formatBytes(maxSize)}`);
            return;
        }
        // Validate file type
        if (!acceptedTypes.includes(file.type)) {
            setError(`File type not supported. Allowed: ${acceptedTypes.map(t => t.split('/')[1]).join(', ')}`);
            return;
        }
        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setPreview(reader.result);
        };
        reader.readAsDataURL(file);
        setSelectedFile(file);
        onFileSelected?.(file);
    };
    const handleUpload = async () => {
        if (!selectedFile || !onUpload)
            return;
        setUploading(true);
        setError(null);
        try {
            const result = await onUpload(selectedFile);
            if (result.success) {
                setSuccess(true);
                setTimeout(() => setSuccess(false), 3000);
                if (result.url) {
                    setPreview(result.url);
                }
            }
            else {
                setError(result.error || 'Upload failed');
            }
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Upload failed');
        }
        finally {
            setUploading(false);
            setSelectedFile(null);
        }
    };
    const handleRemove = () => {
        setPreview(null);
        setSelectedFile(null);
        setError(null);
        setSuccess(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        onRemove?.();
    };
    const formatBytes = (bytes) => {
        if (bytes === 0)
            return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };
    return (_jsxs("div", { className: "space-y-3", children: [label && (_jsx("label", { className: "block text-sm font-medium text-adaptive-primary", children: label })), _jsxs("div", { className: "flex items-start gap-4", children: [_jsxs("div", { className: `${sizeClasses[size]} ${shapeClasses[shape]} border-2 border-dashed border-gray-300 dark:border-gray-700 relative overflow-hidden bg-gray-50 dark:bg-gray-900 flex items-center justify-center`, children: [preview ? (_jsxs(_Fragment, { children: [_jsx("img", { src: preview, alt: "Preview", className: "w-full h-full object-cover" }), _jsx("button", { onClick: handleRemove, disabled: disabled || uploading, className: "absolute top-2 right-2 p-1.5 bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors disabled:opacity-50", title: "Remove image", children: _jsx(X, { className: "w-4 h-4" }) })] })) : (_jsxs("button", { onClick: () => fileInputRef.current?.click(), disabled: disabled, className: "w-full h-full flex flex-col items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors disabled:opacity-50", children: [_jsx(ImageIcon, { className: "w-8 h-8 mb-2" }), _jsx("span", { className: "text-xs font-medium", children: "Click to upload" })] })), uploading && (_jsx("div", { className: "absolute inset-0 bg-black/50 flex items-center justify-center", children: _jsxs("div", { className: "text-white text-center", children: [_jsx("div", { className: "animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2" }), _jsx("p", { className: "text-xs font-medium", children: "Uploading..." })] }) })), success && (_jsx("div", { className: "absolute inset-0 bg-green-500/90 flex items-center justify-center", children: _jsx(Check, { className: "w-12 h-12 text-white" }) }))] }), _jsxs("div", { className: "flex-1 space-y-2", children: [helperText && (_jsx("p", { className: "text-xs text-adaptive-muted", children: helperText })), _jsx("input", { ref: fileInputRef, type: "file", accept: acceptedTypes.join(','), onChange: handleFileSelect, disabled: disabled, className: "hidden" }), selectedFile && onUpload && !uploading && (_jsxs("button", { onClick: handleUpload, disabled: disabled || uploading, className: "inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50", children: [_jsx(Upload, { className: "w-4 h-4 mr-2" }), "Upload"] })), !selectedFile && preview && (_jsxs("button", { onClick: () => fileInputRef.current?.click(), disabled: disabled, className: "inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-700 text-sm font-medium rounded-md text-adaptive-primary bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50", children: [_jsx(Upload, { className: "w-4 h-4 mr-2" }), "Change"] })), error && (_jsxs("div", { className: "flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg", children: [_jsx(AlertCircle, { className: "w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" }), _jsx("p", { className: "text-xs text-red-600 dark:text-red-400", children: error })] })), success && (_jsxs("div", { className: "flex items-start gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg", children: [_jsx(Check, { className: "w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" }), _jsx("p", { className: "text-xs text-green-600 dark:text-green-400", children: "Upload successful!" })] })), selectedFile && (_jsxs("div", { className: "text-xs text-adaptive-muted", children: [_jsxs("p", { children: [_jsx("strong", { children: "File:" }), " ", selectedFile.name] }), _jsxs("p", { children: [_jsx("strong", { children: "Size:" }), " ", formatBytes(selectedFile.size)] }), _jsxs("p", { children: [_jsx("strong", { children: "Type:" }), " ", selectedFile.type] })] }))] })] })] }));
}
